/* eslint-disable max-len */
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { FilterNoneOutlined } from '@mui/icons-material';
import ImageClass from '../../../assets';
import { RootState } from '../../../redux/store';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

/**
 * @heading1: ShareFormModal
 * @paragraph: A modal component for sharing a form. Allows copying an iframe or a direct URL.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const fallbackCopy = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
        document.execCommand('copy');
    } catch (err) {
        console.error('Fallback: Copy command failed', err);
    }

    document.body.removeChild(textarea);
};

const ShareFormModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    // Get the selected form from Redux
    const { selectedForm } = useSelector((state: RootState) => state.form);
    const [copiedUrl, setCopiedUrl] = useState<boolean>(false);
    const [copiedEmbed, setCopiedEmbed] = useState<boolean>(false);

    // Function to copy text to clipboard
    const copyToClipboard = () => {
        // navigator.clipboard.writeText(`${window.location.origin}/forms/${selectedForm?.form_code}`).then(() => {
        //     //
        // })
        //     .catch(() => {
        //         // console.error("Failed to copy: ", err);
        //     });
        const text = `${window.location.origin}/forms/${selectedForm?.form_code}`;
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
        } else {
            fallbackCopy(text);
        }
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const copyEmbededCode = () => {
        navigator.clipboard.writeText(selectedForm?.embed_code as string).then(() => {
            setCopiedEmbed(true);
            setTimeout(() => setCopiedEmbed(false), 2000);
        })
            .catch(() => {
                // console.error("Failed to copy: ", err);
            });
    };

    const copyToIframe = () => {
        navigator.clipboard.writeText(`<div class="iframe-group" style="width: 500px; height: 500px;"><iframe width="100%" height="100%" src="${window.location.origin}/forms/${selectedForm?.form_code}&embed=yes"></iframe></div>`).then(() => {
            //
        })
            .catch(() => {
                // console.error("Failed to copy: ", err);
            });
    };

    const copyToSnappsClipboard = (value: string) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(value).catch(() => fallbackCopy(value));
        } else {
            fallbackCopy(value);
        }
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    return (
        <CustomModal open={open} handleClose={onClose} title="Share your Form">
            {selectedForm?.type === 'elementor-manual' || selectedForm?.type === 'snapps-manual'
                ? <Box display="flex" flexDirection="column" gap={2}>
                    <Box sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        padding: '5px 0',
                        color: 'rgba(51,56,63,1)',
                        borderRadius: '10px'
                    })}>
                        <Box display="flex" alignItems="center" justifyContent="center">
                            <img
                                src={selectedForm?.type === 'snapps-manual' ? ImageClass.snappsLogo : ImageClass.elementorLogo}
                                alt={selectedForm?.type === 'snapps-manual' ? 'Snapps' : 'Elementor'}
                                height="140"
                                width="140" />
                        </Box>
                    </Box>
                    <CustomInput
                        fullWidth
                        value={selectedForm?.type === 'snapps-manual' ? `${process.env.REACT_APP_SNAPPS_FORM_LINK}${selectedForm.form_code}` : `${process.env.REACT_APP_ELEMENTOR_FORM_LINK}${selectedForm.form_code}`}
                        onChange={() => null}
                    />
                    {/* <Button variant="outlined" sx={{ minWidth: '140px' }} onClick={() => handleCopy(codes.snapps_code)}>Copy Webhook Link</Button> */}
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<FilterNoneOutlined />}
                        fullWidth
                        data-screenshot={selectedForm?.type === 'snapps-manual' ? `${process.env.REACT_APP_SNAPPS_FORM_LINK}${selectedForm.form_code}` : `${process.env.REACT_APP_ELEMENTOR_FORM_LINK}${selectedForm.form_code}`}
                        onClick={() => copyToSnappsClipboard(selectedForm?.type === 'snapps-manual' ? `${process.env.REACT_APP_SNAPPS_FORM_LINK}${selectedForm.form_code}` : `${process.env.REACT_APP_ELEMENTOR_FORM_LINK}${selectedForm.form_code}`)}
                    >
                        {copiedUrl ? 'Copied!' : 'Copy Webhook Link'}
                    </Button>
                </Box>
                : <>
                    <Typography variant="subtitle1" color="textDisabled" align="center" fontWeight="500">
                        Your form is live! Copy either link to share on social media, messaging apps, email, or websites.
                    </Typography>
                    <Box display="flex" gap={2} mt={2}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FilterNoneOutlined />}
                            fullWidth
                            onClick={() => copyToIframe()}
                            sx={{ display: 'none' }}
                        >
                            Copy iframe
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FilterNoneOutlined />}
                            fullWidth
                            onClick={() => copyEmbededCode()}
                        >
                            {copiedEmbed ? 'Copied!' : 'Copy Embed'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<FilterNoneOutlined />}
                            fullWidth
                            data-screenshot={`${window.location.origin}/forms/${selectedForm?.form_code}`}
                            onClick={() => copyToClipboard()}
                        >
                            {copiedUrl ? 'Copied!' : 'Copy URL'}
                        </Button>
                    </Box></>}
        </CustomModal>
    );
};

export default ShareFormModal;
